import { useEffect } from 'react';
import useAuth from 'hooks/useAuth';
import { startGuestPresenceResponder } from 'utils/guestSession';

/**
 * useGuestSessionLifecycle
 *
 * Keeps this tab available as a responder to cross-tab presence pings for
 * the duration of its lifetime in the QR-facing routes. Concretely, it
 * listens on a shared `BroadcastChannel` and replies "pong" whenever a
 * newly-opened sibling tab broadcasts a "ping" from `prepareGuestSession`.
 *
 * This is what preserves the multi-tab guest flow: when `QuickAccess`
 * calls `window.open('/forms-qr/add-response/...')`, the new tab runs its
 * fresh-load presence check and gets a pong from this tab, so it skips
 * token cleanup and joins the existing session.
 *
 * No close / reload / heartbeat logic lives here on purpose. See
 * `src/utils/guestSession.ts` for the rationale: any "clear on close"
 * strategy is unreliable on mobile browsers (the OS can kill the tab
 * before `pagehide` fires), so cleanup happens on the NEXT page load
 * instead - driven entirely by `prepareGuestSession` in
 * `GuestSessionGate`. All this hook needs to do is stay reachable via
 * BroadcastChannel while it is mounted.
 *
 * For non-guest users this is a no-op.
 */
export const useGuestSessionLifecycle = () => {
  const { isGuestUser } = useAuth();

  useEffect(() => {
    if (!isGuestUser) return undefined;
    return startGuestPresenceResponder();
  }, [isGuestUser]);
};
